import React, { useState, useSyncExternalStore } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldAlert } from 'lucide-react';
import {
  getExpectedSupabaseAuth,
  isSupabaseSessionLost,
  markSupabaseSessionRestored,
  subscribeSupabaseSessionLost,
} from '../../services/supabaseClient';

/** True while the Supabase client has no Clerk token for the signed-in user. */
export const useSupabaseSessionLost = () =>
  useSyncExternalStore(subscribeSupabaseSessionLost, isSupabaseSessionLost);

// Shown instead of the signed-in app when Clerk dropped the session in the
// browser (see services/supabaseClient.ts). Nothing is saved meanwhile, so the
// user restores the session before carrying on rather than editing into a void.
export const SessionLostNotice: React.FC = () => {
  const { t } = useTranslation();
  const clerk = useClerk();
  const [state, setState] = useState<'idle' | 'restoring' | 'failed'>('idle');

  const restore = async () => {
    setState('restoring');
    try {
      const expected = getExpectedSupabaseAuth();
      if (!clerk.session && expected?.sessionId) await clerk.setActive({ session: expected.sessionId });
      const template = import.meta.env.VITE_CLERK_JWT_TEMPLATE || 'supabase';
      const token = await clerk.session?.getToken({ template });
      if (!token) throw new Error('No session token');
      markSupabaseSessionRestored();
      setState('idle');
    } catch (err) {
      console.warn('[Auth] Restoring the session failed:', err);
      setState('failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1E] flex items-center justify-center p-6">
      <div role="alertdialog" aria-labelledby="session-lost-title" className="w-full max-w-md rounded-[2rem] border border-amber-500/30 bg-[#0F172A] p-8 text-center shadow-2xl">
        <ShieldAlert className="mx-auto text-amber-400" size={40} aria-hidden="true" />
        <h1 id="session-lost-title" className="mt-4 text-xl font-black uppercase tracking-tighter text-white">
          {t('session.lost_title', { defaultValue: 'Session interrupted' })}
        </h1>
        <p className="mt-3 text-sm text-slate-300">
          {t('session.lost_body', { defaultValue: "CartLens couldn't refresh your sign-in, so nothing is being saved right now. Try again to pick up where you left off." })}
        </p>
        {state === 'failed' && (
          <p className="mt-3 text-sm text-amber-300" role="status">
            {t('session.restore_failed', { defaultValue: "Still couldn't restore your session. Reload the page, or sign in again if asked." })}
          </p>
        )}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={restore}
            disabled={state === 'restoring'}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-[#0A0F1E] hover:bg-emerald-400 disabled:opacity-60"
          >
            {state === 'restoring' && <Loader2 className="animate-spin" size={16} aria-hidden="true" />}
            {state === 'restoring'
              ? t('session.restoring', { defaultValue: 'Restoring…' })
              : t('session.retry', { defaultValue: 'Try again' })}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-full border border-white/15 px-6 py-3 text-sm font-bold text-white hover:bg-white/5"
          >
            {t('session.reload', { defaultValue: 'Reload page' })}
          </button>
        </div>
      </div>
    </div>
  );
};
